import { CloudOff, RefreshCw, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@up2d8/ui';
import { useOffline } from '../../offline/use-offline';
import { formatDateTime } from '../../shared/format';

/**
 * De strook boven de feed: verbinding, wachtrij en een nieuwe versie (Stap 12).
 *
 * Drie berichten die alleen verschijnen als ze iets te zeggen hebben. Dat is de hele ontwerpregel
 * hier: een vaste balk die meestal "alles in orde" zegt, wordt binnen een week niet meer gelezen — en
 * dan staat hij er ook niet meer als het wél misgaat.
 *
 * De volgorde is die van urgentie voor de medewerker:
 *
 * 1. **Geen verbinding**, met hoe oud wat hij ziet is. Zonder dat tweede is "offline" een mededeling
 *    zonder gevolg; mét die tijd kan hij zelf beslissen of hij erop vertrouwt.
 * 2. **Er wacht nog iets op verzending.** Zijn bevestiging is niet kwijt, en dat hoort hij te weten.
 * 3. **Er is een nieuwe versie.** Dat kan wachten tot hij tijd heeft.
 */
export function OfflineBar() {
  const { t } = useTranslation();
  const { online, lastSyncedAt, pending, updateReady, applyUpdate } = useOffline();

  const niets = online && pending.size === 0 && !updateReady;
  if (niets) return null;

  return (
    <div className="flex flex-col gap-2" role="status" aria-live="polite">
      {!online && (
        <div className="flex items-start gap-2 rounded-lg bg-warning-tint px-3 py-2">
          <CloudOff aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-text-primary" />
          <div>
            <p className="text-content font-semibold text-text-primary">{t('offline.title')}</p>
            <p className="text-small text-text-secondary">
              {lastSyncedAt
                ? t('offline.lastUpdated', {
                    when: formatDateTime(lastSyncedAt),
                  })
                : t('offline.noDataYet')}
            </p>
          </div>
        </div>
      )}

      {pending.size > 0 && (
        <div className="flex items-start gap-2 rounded-lg bg-brand-primary-tint px-3 py-2">
          <Upload aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary" />
          <p className="text-content text-text-primary">
            {t('offline.queued', { count: pending.size })}
          </p>
        </div>
      )}

      {updateReady && (
        <div className="flex flex-wrap items-center justify-between gap-element rounded-lg bg-background px-3 py-2">
          <p className="text-content text-text-primary">{t('offline.updateReady')}</p>
          <Button size="sm" onClick={applyUpdate}>
            <RefreshCw aria-hidden className="h-4 w-4" />
            {t('offline.updateNow')}
          </Button>
        </div>
      )}
    </div>
  );
}
